<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 overflow-y-auto"
  >
    <div class="bg-white rounded-t-2xl sm:rounded-lg w-full max-w-5xl p-4 sm:p-6 my-0 sm:my-8 max-h-[92vh] overflow-y-auto shadow-xl">
      <div class="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 class="font-semibold text-lg text-gray-900">
            {{ $t('smm.ideas.title') }}
            <span v-if="network" class="font-normal text-gray-500">· {{ network.name }}</span>
          </h3>
          <p class="text-sm text-gray-500 mt-0.5">{{ $t('smm.ideas.subtitle') }}</p>
        </div>
        <button
          type="button"
          class="shrink-0 text-gray-400 hover:text-gray-700 text-xl leading-none"
          :aria-label="$t('common.close')"
          @click="close"
        >
          ×
        </button>
      </div>

      <div class="flex flex-wrap items-center justify-between gap-2 mb-4">
        <p class="text-sm text-gray-500">
          {{ $t('smm.ideas.count', { n: ideas.length }) }}
        </p>
        <button
          type="button"
          class="px-3 py-1.5 text-sm rounded-lg bg-amber-500 text-white hover:bg-amber-600"
          @click="startCreate"
        >
          {{ $t('smm.ideas.add') }}
        </button>
      </div>

      <p v-if="error" class="text-sm text-red-600 mb-3 whitespace-pre-line">{{ error }}</p>
      <div v-if="loading" class="p-8 text-center text-gray-500">{{ $t('common.loading') }}</div>

      <div v-else class="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-4">
        <div class="space-y-2">
          <div
            v-if="!ideas.length"
            class="p-6 text-center text-sm text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200"
          >
            {{ $t('smm.ideas.empty') }}
          </div>
          <button
            v-for="idea in ideas"
            :key="idea.id"
            type="button"
            class="w-full text-left rounded-lg border p-3 transition"
            :class="editingId === idea.id
              ? 'border-amber-500 bg-amber-50/40 shadow-sm'
              : 'border-gray-100 bg-white hover:border-gray-300'"
            @click="startEdit(idea)"
          >
            <div class="flex items-start justify-between gap-2">
              <p class="font-medium text-gray-800 text-sm leading-snug">
                {{ idea.title || $t('smm.ideas.untitled') }}
              </p>
              <span class="shrink-0 text-[10px] text-gray-400">
                {{ fileCountLabel(idea) }}
              </span>
            </div>
            <p v-if="idea.body" class="text-xs text-gray-500 line-clamp-2 mt-1">{{ idea.body }}</p>
            <div v-if="ideaImages(idea).length" class="flex gap-1 mt-2">
              <img
                v-for="file in ideaImages(idea).slice(0, 4)"
                :key="file.id"
                :src="file.url"
                :alt="file.original_name || ''"
                class="w-10 h-10 rounded object-cover border border-gray-100"
              >
            </div>
          </button>
        </div>

        <div class="rounded-lg border border-gray-200 bg-gray-50/50 p-4 space-y-3">
          <div v-if="!formOpen" class="p-6 text-center text-sm text-gray-500">
            {{ $t('smm.ideas.select_or_add') }}
          </div>
          <template v-else>
            <h4 class="font-medium text-gray-800">
              {{ editingId ? $t('smm.ideas.edit_title') : $t('smm.ideas.new_title') }}
            </h4>

            <div>
              <label class="text-sm text-gray-600">{{ $t('smm.ideas.field_title') }}</label>
              <input
                v-model="form.title"
                type="text"
                class="w-full border rounded-lg p-2.5 mt-1 text-sm bg-white"
                :placeholder="$t('smm.ideas.field_title_placeholder')"
              >
            </div>

            <div>
              <label class="text-sm text-gray-600">{{ $t('smm.ideas.field_body') }}</label>
              <textarea
                v-model="form.body"
                rows="5"
                class="w-full border rounded-lg p-2.5 mt-1 text-sm resize-y bg-white"
                :placeholder="$t('smm.ideas.field_body_placeholder')"
              />
            </div>

            <div>
              <div class="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                <label class="text-sm text-gray-600">{{ $t('smm.ideas.images') }}</label>
                <button
                  type="button"
                  class="text-xs px-2 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50"
                  @click="pasteFromClipboard"
                >
                  {{ $t('smm.upload.paste_clipboard') }}
                </button>
              </div>
              <div
                ref="pasteZone"
                class="border-2 border-dashed rounded-lg p-3 text-center transition outline-none cursor-pointer"
                :class="pasteZoneClass"
                tabindex="0"
                @focus="pasteFocused = true"
                @blur="pasteFocused = false"
                @paste="onPaste"
                @dragover.prevent="dragOver = true"
                @dragleave.prevent="dragOver = false"
                @drop.prevent="onDrop"
                @click="focusPasteZone"
              >
                <p class="text-sm text-gray-600">{{ $t('smm.ideas.paste_hint') }}</p>
                <p class="text-xs text-gray-400 mt-1">{{ $t('smm.upload.ctrl_v_hint') }}</p>
              </div>

              <div v-if="imageItems.length" class="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                <div
                  v-for="(item, index) in imageItems"
                  :key="item.key"
                  class="relative aspect-square rounded-md overflow-hidden border bg-white"
                >
                  <img :src="item.url" :alt="item.name" class="w-full h-full object-cover">
                  <button
                    type="button"
                    class="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-600 text-white text-xs leading-none"
                    @click="removeImageItem(index)"
                  >
                    ×
                  </button>
                  <span
                    v-if="item.saved"
                    class="absolute bottom-1 left-1 text-[9px] px-1 rounded bg-black/50 text-white"
                  >
                    {{ $t('smm.ideas.saved') }}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <div class="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                <label class="text-sm text-gray-600">{{ $t('smm.ideas.files') }}</label>
                <label class="text-xs px-2 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer">
                  {{ $t('smm.ideas.attach_files') }}
                  <input
                    ref="fileInput"
                    type="file"
                    multiple
                    class="hidden"
                    @change="onFileInput"
                  >
                </label>
              </div>
              <ul v-if="fileItems.length" class="space-y-1.5">
                <li
                  v-for="(item, index) in fileItems"
                  :key="item.key"
                  class="flex items-center gap-2 text-sm bg-white border rounded-lg px-2.5 py-1.5"
                >
                  <span class="truncate flex-1 text-gray-700">{{ item.name }}</span>
                  <span v-if="item.saved" class="text-[10px] text-gray-400">{{ $t('smm.ideas.saved') }}</span>
                  <button
                    type="button"
                    class="text-gray-400 hover:text-red-600"
                    @click="removeFileItem(index)"
                  >
                    ×
                  </button>
                </li>
              </ul>
              <p v-else class="text-xs text-gray-400">{{ $t('smm.ideas.files_empty') }}</p>
            </div>

            <p v-if="formError" class="text-sm text-red-600 whitespace-pre-line">{{ formError }}</p>

            <div class="flex flex-wrap justify-end gap-2 pt-2 border-t border-gray-200">
              <button
                v-if="editingId"
                type="button"
                class="mr-auto px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                :disabled="saving"
                @click="removeIdea"
              >
                {{ $t('common.delete') }}
              </button>
              <button
                type="button"
                class="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                @click="cancelForm"
              >
                {{ $t('common.cancel') }}
              </button>
              <button
                type="button"
                class="px-4 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
                :disabled="saving"
                @click="save"
              >
                {{ saving ? $t('common.saving') : $t('common.save') }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
function isImageFile(file) {
  return file && String(file.type || '').startsWith('image/')
}

function extFromMime(mime) {
  const map = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
  }
  return map[(mime || '').toLowerCase()] || 'png'
}

export default {
  name: 'NetworkIdeasModal',
  props: {
    show: { type: Boolean, default: false },
    network: { type: Object, default: null },
  },
  emits: ['close', 'changed'],
  data: () => ({
    loading: false,
    saving: false,
    error: null,
    formError: null,
    ideas: [],
    formOpen: false,
    editingId: null,
    form: { title: '', body: '' },
    pendingImages: [],
    pendingFiles: [],
    existingFiles: [],
    removedExistingIds: [],
    pasteFocused: false,
    dragOver: false,
  }),
  computed: {
    pasteZoneClass() {
      if (this.dragOver) return 'border-amber-500 bg-amber-50'
      if (this.pasteFocused) return 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
      return 'border-gray-300 bg-white hover:border-gray-400'
    },
    imageItems() {
      const existing = this.existingFiles
        .filter((f) => f.type === 'image' && !this.removedExistingIds.includes(f.id))
        .map((f) => ({
          key: `saved-img-${f.id}`,
          url: f.url,
          name: f.original_name || 'image',
          saved: true,
          id: f.id,
        }))
      const pending = this.pendingImages.map((item, i) => ({
        key: item.key || `pending-img-${i}`,
        url: item.url,
        name: item.file?.name || 'image',
        saved: false,
        pendingIndex: i,
      }))
      return [...existing, ...pending]
    },
    fileItems() {
      const existing = this.existingFiles
        .filter((f) => f.type !== 'image' && !this.removedExistingIds.includes(f.id))
        .map((f) => ({
          key: `saved-file-${f.id}`,
          name: f.original_name || 'file',
          saved: true,
          id: f.id,
        }))
      const pending = this.pendingFiles.map((item, i) => ({
        key: item.key || `pending-file-${i}`,
        name: item.file?.name || 'file',
        saved: false,
        pendingIndex: i,
      }))
      return [...existing, ...pending]
    },
  },
  watch: {
    show(value) {
      if (value && this.network?.id) {
        this.resetForm()
        this.load()
        this.$nextTick(() => {
          window.addEventListener('paste', this.onWindowPaste)
        })
      } else {
        window.removeEventListener('paste', this.onWindowPaste)
        this.clearPending()
      }
    },
  },
  beforeUnmount() {
    window.removeEventListener('paste', this.onWindowPaste)
    this.clearPending()
  },
  methods: {
    close() {
      this.$emit('close')
    },
    ideaImages(idea) {
      return (idea.files || []).filter((f) => f.type === 'image')
    },
    fileCountLabel(idea) {
      const n = idea.files?.length || 0
      return this.$t('smm.ideas.files_count', { n })
    },
    focusPasteZone() {
      this.$refs.pasteZone?.focus()
    },
    resetForm() {
      this.formOpen = false
      this.editingId = null
      this.form = { title: '', body: '' }
      this.formError = null
      this.existingFiles = []
      this.removedExistingIds = []
      this.clearPending()
    },
    clearPending() {
      this.pendingImages.forEach((item) => {
        if (item.url) URL.revokeObjectURL(item.url)
      })
      this.pendingImages = []
      this.pendingFiles = []
    },
    startCreate() {
      this.resetForm()
      this.formOpen = true
      this.$nextTick(() => this.focusPasteZone())
    },
    startEdit(idea) {
      this.clearPending()
      this.formOpen = true
      this.editingId = idea.id
      this.form = {
        title: idea.title || '',
        body: idea.body || '',
      }
      this.existingFiles = [...(idea.files || [])]
      this.removedExistingIds = []
      this.formError = null
      this.$nextTick(() => this.focusPasteZone())
    },
    cancelForm() {
      this.resetForm()
    },
    async load() {
      if (!this.network?.id) return
      this.loading = true
      this.error = null
      try {
        const { data } = await axios.get(`/api/v1/smm/social-networks/${this.network.id}/ideas`)
        this.ideas = Array.isArray(data?.data) ? data.data : []
        if (this.editingId) {
          const current = this.ideas.find((i) => i.id === this.editingId)
          if (current) this.startEdit(current)
        }
      } catch (e) {
        this.error = e.response?.data?.message || e.message || this.$t('common.error')
        this.ideas = []
      } finally {
        this.loading = false
      }
    },
    extractImageFiles(clipboardOrTransfer) {
      const items = clipboardOrTransfer?.items
      if (!items) return []
      const files = []
      for (const item of items) {
        if (item.kind === 'file') {
          const file = item.getAsFile()
          if (file && isImageFile(file)) files.push(file)
        }
      }
      return files
    },
    addPendingImages(files) {
      for (const file of files) {
        if (!isImageFile(file)) continue
        this.pendingImages.push({
          key: `img-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          file,
          url: URL.createObjectURL(file),
        })
      }
    },
    addPendingFiles(files) {
      for (const file of files) {
        if (isImageFile(file)) {
          this.addPendingImages([file])
          continue
        }
        this.pendingFiles.push({
          key: `file-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          file,
        })
      }
    },
    onPaste(e) {
      if (!this.formOpen) return
      const files = this.extractImageFiles(e.clipboardData)
      if (!files.length) return
      e.preventDefault()
      this.addPendingImages(files)
    },
    onWindowPaste(e) {
      if (!this.show || !this.formOpen) return
      const target = e.target
      const tag = target?.tagName?.toLowerCase()
      if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) return
      const files = this.extractImageFiles(e.clipboardData)
      if (!files.length) return
      e.preventDefault()
      this.addPendingImages(files)
    },
    async pasteFromClipboard() {
      this.focusPasteZone()
      if (navigator.clipboard?.read) {
        try {
          const clipItems = await navigator.clipboard.read()
          const files = []
          for (const item of clipItems) {
            const type = item.types.find((t) => t.startsWith('image/'))
            if (!type) continue
            const blob = await item.getType(type)
            const ext = extFromMime(type)
            files.push(new File([blob], `clipboard-${Date.now()}.${ext}`, { type }))
          }
          if (files.length) {
            this.addPendingImages(files)
            return
          }
        } catch (_) {
          // fall through to hint
        }
      }
      this.formError = this.$t('smm.ideas.paste_manual_hint')
    },
    onDrop(e) {
      this.dragOver = false
      const files = Array.from(e.dataTransfer?.files || [])
      this.addPendingFiles(files)
    },
    onFileInput(e) {
      const files = Array.from(e.target.files || [])
      this.addPendingFiles(files)
      e.target.value = ''
    },
    async removeImageItem(index) {
      const item = this.imageItems[index]
      if (!item) return
      if (item.saved) {
        await this.deleteExistingFile(item.id)
        return
      }
      const pending = this.pendingImages[item.pendingIndex]
      if (pending?.url) URL.revokeObjectURL(pending.url)
      this.pendingImages.splice(item.pendingIndex, 1)
    },
    async removeFileItem(index) {
      const item = this.fileItems[index]
      if (!item) return
      if (item.saved) {
        await this.deleteExistingFile(item.id)
        return
      }
      this.pendingFiles.splice(item.pendingIndex, 1)
    },
    async deleteExistingFile(fileId) {
      if (!this.editingId) {
        this.removedExistingIds = [...this.removedExistingIds, fileId]
        this.existingFiles = this.existingFiles.filter((f) => f.id !== fileId)
        return
      }
      try {
        const { data } = await axios.delete(`/api/v1/smm/ideas/${this.editingId}/files/${fileId}`)
        if (data.idea) {
          this.replaceIdea(data.idea)
          this.existingFiles = [...(data.idea.files || [])]
        } else {
          this.existingFiles = this.existingFiles.filter((f) => f.id !== fileId)
        }
        this.$emit('changed')
      } catch (e) {
        this.formError = e.response?.data?.message || this.$t('common.delete_error')
      }
    },
    replaceIdea(idea) {
      const idx = this.ideas.findIndex((i) => i.id === idea.id)
      if (idx >= 0) {
        const next = [...this.ideas]
        next[idx] = idea
        this.ideas = next
      } else {
        this.ideas = [idea, ...this.ideas]
      }
    },
    async save() {
      if (!this.network?.id || this.saving) return
      this.saving = true
      this.formError = null
      try {
        if (this.editingId) {
          const { data } = await axios.patch(`/api/v1/smm/ideas/${this.editingId}`, {
            title: this.form.title.trim() || null,
            body: this.form.body.trim() || null,
          })
          let idea = data.data
          const pending = [...this.pendingImages.map((i) => i.file), ...this.pendingFiles.map((i) => i.file)]
          if (pending.length) {
            const fd = new FormData()
            pending.forEach((file, i) => fd.append(`files[${i}]`, file))
            const uploaded = await axios.post(`/api/v1/smm/ideas/${this.editingId}/files`, fd)
            idea = uploaded.data.idea || idea
          }
          this.replaceIdea(idea)
          this.startEdit(idea)
        } else {
          const fd = new FormData()
          if (this.form.title.trim()) fd.append('title', this.form.title.trim())
          if (this.form.body.trim()) fd.append('body', this.form.body.trim())
          const pending = [...this.pendingImages.map((i) => i.file), ...this.pendingFiles.map((i) => i.file)]
          pending.forEach((file, i) => fd.append(`files[${i}]`, file))
          const { data } = await axios.post(`/api/v1/smm/social-networks/${this.network.id}/ideas`, fd)
          this.replaceIdea(data.data)
          this.startCreate()
        }
        this.clearPending()
        this.$emit('changed')
      } catch (e) {
        this.formError = e.response?.data?.message
          || (e.response?.data?.errors ? Object.values(e.response.data.errors).flat().join('\n') : null)
          || this.$t('common.save_error')
      } finally {
        this.saving = false
      }
    },
    async removeIdea() {
      if (!this.editingId || !confirm(this.$t('smm.ideas.delete_confirm'))) return
      try {
        await axios.delete(`/api/v1/smm/ideas/${this.editingId}`)
        this.ideas = this.ideas.filter((i) => i.id !== this.editingId)
        this.resetForm()
        this.$emit('changed')
      } catch (e) {
        this.formError = e.response?.data?.message || this.$t('common.delete_error')
      }
    },
  },
}
</script>
